import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/utils';

/** 状態の色（success: 成功・公開中 / warning: 要対応 / danger: 失敗 / neutral: 未設定・実行中など）。 */
export type StatusTone = 'success' | 'warning' | 'danger' | 'neutral';

const TONE_TEXT: Record<StatusTone, string> = {
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  neutral: 'text-muted-foreground',
};

/** 公開状態のピル（色の点 + ラベル）。 */
export function StatusChip({
  tone,
  children,
}: {
  tone: 'success' | 'neutral';
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold',
        tone === 'success' ? 'bg-success-muted text-success' : 'bg-muted text-muted-foreground',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'size-[7px] shrink-0 rounded-full',
          tone === 'success' ? 'bg-success' : 'ring-[1.5px] ring-inset ring-muted-foreground/60',
        )}
      />
      {children}
    </span>
  );
}

/** 状態の 1 行目（アイコン + ラベル）。 */
export function StatusLine({
  tone,
  icon,
  children,
  title,
}: {
  tone: StatusTone;
  icon: ReactNode;
  children: ReactNode;
  title?: string;
}) {
  return (
    <div
      title={title}
      className={cn(
        'flex items-center gap-1.5 whitespace-nowrap text-[13px] [&_svg]:size-[15px] [&_svg]:shrink-0',
        tone === 'neutral' ? 'font-normal' : 'font-medium',
        TONE_TEXT[tone],
      )}
    >
      {icon}
      {children}
    </div>
  );
}

/** 状態の 2 行目（日時・補足・操作リンク）。 */
export function StatusNote({
  tone = 'neutral',
  children,
}: {
  tone?: StatusTone;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'mt-1 flex items-center gap-1 whitespace-nowrap text-xs [&_svg]:size-[13px] [&_svg]:shrink-0',
        TONE_TEXT[tone],
      )}
    >
      {children}
    </div>
  );
}

/** 状態の補足に置く、文中リンクの見た目のボタン。 */
export function InlineLinkButton({ className, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        'font-medium text-primary underline underline-offset-2 hover:text-primary/80 disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      {...props}
    />
  );
}
